import { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import UpgradeModal from "@/components/UpgradeModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Play, Square, Plus, Trash2, Edit, CheckCircle, XCircle } from "lucide-react";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";

export default function TimeTracking() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "de" ? de : enUS;
  const [projects, setProjects] = useState([]);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [timeEntries, setTimeEntries] = useState([]);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerDuration, setTimerDuration] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showBilled, setShowBilled] = useState("all"); // all, billed, unbilled
  const [formData, setFormData] = useState({
    project_id: "",
    description: "",
    start_time: "",
    end_time: "",
  });

  useEffect(() => {
    fetchProjects();
    fetchTimeEntries();
    fetchActiveTimer();
  }, []);

  useEffect(() => {
    let interval;
    if (activeTimer) {
      interval = setInterval(() => {
        const start = new Date(activeTimer.start_time);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);
        setTimerDuration(diff);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data.filter(p => p.status === "active"));
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const response = await api.get('/time-entries');
      const sorted = response.data.sort((a, b) => 
        new Date(b.start_time) - new Date(a.start_time)
      );
      setTimeEntries(sorted);
    } catch (error) {
      console.error("Error fetching time entries:", error);
    }
  };

  const fetchActiveTimer = async () => {
    try {
      const response = await api.get('/time-entries/active');
      setActiveTimer(response.data);
    } catch (error) {
      console.error("Error fetching active timer:", error);
    }
  };

  const startTimer = async (projectId, description) => {
    try {
      const response = await api.post(
        `/time-entries/start?project_id=${projectId}&description=${encodeURIComponent(description)}`
      );
      setActiveTimer(response.data);
      toast.success("Timer gestartet");
      fetchTimeEntries();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error(error.response?.data?.detail || "Fehler beim Starten des Timers");
      }
    }
  };

  const stopTimer = async () => {
    try {
      await api.post('/time-entries/stop');
      setActiveTimer(null);
      setTimerDuration(0);
      toast.success("Timer gestoppt");
      fetchTimeEntries();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Fehler beim Stoppen des Timers");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
      };

      if (editingEntry) {
        await api.put(`/time-entries/${editingEntry.id}`, data);
        toast.success("Zeiteintrag aktualisiert");
      } else {
        await api.post(`/time-entries`, data);
        toast.success("Zeiteintrag erstellt");
      }

      setDialogOpen(false);
      setEditingEntry(null);
      setFormData({ project_id: "", description: "", start_time: "", end_time: "" });
      fetchTimeEntries();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Fehler beim Speichern");
      }
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm(t('timeTracking.confirmDelete', 'Delete this entry?'))) return;
    try {
      await api.delete(`/time-entries/${id}`);
      toast.success(t('timeTracking.entryDeleted', 'Time entry deleted'));
      fetchTimeEntries();
    } catch (error) {
      toast.error(t('common.deleteError', 'Error deleting'));
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };


  const calculateStats = () => {
    const billed = timeEntries.filter(e => e.invoiced && e.duration_minutes);
    const unbilled = timeEntries.filter(e => !e.invoiced && e.duration_minutes);
    
    const billedHours = billed.reduce((sum, e) => sum + e.duration_minutes, 0) / 60;
    const unbilledHours = unbilled.reduce((sum, e) => sum + e.duration_minutes, 0) / 60;
    
    return {
      billedHours: billedHours.toFixed(1),
      unbilledHours: unbilledHours.toFixed(1),
      billedCount: billed.length,
      unbilledCount: unbilled.length
    };
  };

  const getFilteredEntries = () => {
    if (showBilled === "billed") {
      return timeEntries.filter(e => e.invoiced);
    } else if (showBilled === "unbilled") {
      return timeEntries.filter(e => !e.invoiced);
    }
    return timeEntries;
  };

  return (
    <div className="space-y-8" data-testid="time-tracking-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('timeTracking.title')}</h1>
          <p className="text-lg text-gray-600">{t('timeTracking.subtitle')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-time-entry-button" onClick={() => {
              setEditingEntry(null);
              setFormData({ project_id: "", description: "", start_time: "", end_time: "" });
            }}>
              <Plus className="h-4 w-4 mr-2" />
              {t('timeTracking.manualEntry')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingEntry ? t('timeTracking.editEntry') : t('timeTracking.newEntry')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t('timeTracking.project')} *</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                  required
                >
                  <SelectTrigger data-testid="project-select">
                    <SelectValue placeholder={t('timeTracking.selectProject')} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('timeTracking.description')}</Label>
                <Input
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('timeTracking.descriptionPlaceholder')}
                />
              </div>
              <div>
                <Label>{t('timeTracking.startTime')} *</Label>
                <Input
                  data-testid="start-time-input"
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('timeTracking.endTime')} *</Label>
                <Input
                  data-testid="end-time-input"
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" className="w-full" data-testid="save-time-entry-button">
                {editingEntry ? t('timeTracking.update') : t('timeTracking.create')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>


      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('timeTracking.totalHours')}</CardTitle>
            <Clock className="h-5 w-5 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {(parseFloat(calculateStats().billedHours) + parseFloat(calculateStats().unbilledHours)).toFixed(1)}h
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {calculateStats().billedCount + calculateStats().unbilledCount} {t('timeTracking.entries')}
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover border-green-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('timeTracking.billed')}</CardTitle>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {calculateStats().billedHours}h
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {calculateStats().billedCount} {t('timeTracking.entries')}
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover border-orange-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('timeTracking.unbilled')}</CardTitle>
            <XCircle className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {calculateStats().unbilledHours}h
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {calculateStats().unbilledCount} {t('timeTracking.entries')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Timer Widget */}
      <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="pt-6">
          {activeTimer ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-gray-700">{t('timeTracking.timerRunning')}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{activeTimer.project_name}</h3>
                  {activeTimer.description && (
                    <p className="text-gray-600">{activeTimer.description}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold text-emerald-700" data-testid="timer-display">
                    {formatDuration(timerDuration)}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Gestartet: {format(new Date(activeTimer.start_time), "HH:mm", { locale })}
                  </p>
                </div>
              </div>
              <Button
                data-testid="stop-timer-button"
                onClick={stopTimer}
                className="w-full bg-red-600 hover:bg-red-700"
                size="lg"
              >
                <Square className="h-5 w-5 mr-2" fill="white" />
                {t('timeTracking.stopTimer')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <Clock className="h-16 w-16 mx-auto text-emerald-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Kein aktiver Timer</h3>
                <p className="text-gray-600">{t('timeTracking.selectProjectToStart')}</p>
              </div>
              <div className="space-y-3">
                <Label>{t('timeTracking.project')} auswählen</Label>
                <Select
                  onValueChange={(value) => {
                    const selectedProject = projects.find(p => p.id === value);
                    if (selectedProject) {
                      startTimer(value, "");
                    }
                  }}
                >
                  <SelectTrigger className="w-full h-12" data-testid="timer-project-select">
                    <SelectValue placeholder="Projekt auswählen und Timer starten..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        <div className="flex items-center">
                          <Play className="h-4 w-4 mr-2 text-emerald-600" />
                          {project.name}
                        </div>
                      </SelectItem>
                    ))}
                    {projects.length === 0 && (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        Keine aktiven Projekte vorhanden. Erstellen Sie zuerst ein Projekt.
                      </div>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  💡 Wählen Sie ein Projekt aus, um den Timer sofort zu starten
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Time Entries with Tabs */}
      <Tabs defaultValue="all" className="space-y-6" onValueChange={(value) => setShowBilled(value)}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">Alle</TabsTrigger>
          <TabsTrigger value="unbilled" data-testid="tab-unbilled">Offen ({calculateStats().unbilledCount})</TabsTrigger>
          <TabsTrigger value="billed" data-testid="tab-billed">Abgerechnet ({calculateStats().billedCount})</TabsTrigger>
        </TabsList>

        <TabsContent value={showBilled}>
          <Card>
            <CardHeader>
              <CardTitle>{t('timeTracking.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('common.date')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('common.project')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('common.description')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('timeTracking.time')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('timeTracking.duration')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('common.status')}</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredEntries().map((entry) => (
                      <tr key={entry.id} className="border-b hover:bg-gray-50" data-testid={`time-entry-${entry.id}`}>
                        <td className="py-3 px-4">
                          {format(new Date(entry.start_time), "dd.MM.yyyy", { locale: de })}
                        </td>
                        <td className="py-3 px-4 font-medium">{entry.project_name}</td>
                        <td className="py-3 px-4 text-gray-600">{entry.description || "-"}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {format(new Date(entry.start_time), "HH:mm", { locale: de })} -{" "}
                          {entry.end_time ? format(new Date(entry.end_time), "HH:mm", { locale: de }) : t('timeTracking.running', 'Running')}
                        </td>
                        <td className="py-3 px-4 font-semibold text-emerald-600">
                          {entry.duration_minutes ? formatMinutesToHours(entry.duration_minutes) : "-"}
                        </td>
                        <td className="py-3 px-4">
                          {entry.invoiced ? (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              entry.invoice_status === 'paid' ? 'bg-green-100 text-green-800' :
                              entry.invoice_status === 'sent' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {entry.invoice_status === 'paid' ? 'Bezahlt' :
                               entry.invoice_status === 'sent' ? 'Versendet' :
                               entry.invoice_status === 'draft' ? 'Entwurf' :
                               'Abgerechnet'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              <XCircle className="h-3 w-3 mr-1" />
                              Offen
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right space-x-2">
                          <Button
                            data-testid={`edit-entry-${entry.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingEntry(entry);
                              setFormData({
                                project_id: entry.project_id,
                                description: entry.description || "",
                                start_time: format(new Date(entry.start_time), "yyyy-MM-dd'T'HH:mm"),
                                end_time: entry.end_time ? format(new Date(entry.end_time), "yyyy-MM-dd'T'HH:mm") : "",
                              });
                              setDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`toggle-status-${entry.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              try {
                                await api.patch(`/time-entries/${entry.id}/status?invoiced=${!entry.invoiced}`);
                                toast.success(entry.invoiced ? 'Status auf "Offen" gesetzt' : 'Status auf "Abgerechnet" gesetzt');
                                fetchTimeEntries();
                              } catch (error) {
                                toast.error("Fehler beim Ändern des Status");
                              }
                            }}
                            title={entry.invoiced ? "Als offen markieren" : "Als abgerechnet markieren"}
                          >
                            {entry.invoiced ? <XCircle className="h-4 w-4 text-orange-600" /> : <CheckCircle className="h-4 w-4 text-green-600" />}
                          </Button>
                          <Button
                            data-testid={`delete-entry-${entry.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteEntry(entry.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {getFilteredEntries().length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    Keine Zeiteinträge vorhanden
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      

    <UpgradeModal

      isOpen={upgradeModalOpen}

      onClose={() => setUpgradeModalOpen(false)}

      message={upgradeMessage}

      currentPlan={user?.plan}

    />

    </div>
  );
}